import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import FluidSubpageHero from '../components/FluidSubpageHero';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import MedicalClinicSchema from '../components/MedicalClinicSchema';

export default function Kontakt() {
  const { t, lang, getLocalizedValue } = useLanguage();
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await fetch('/_api/public/content/kontakt_side');
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setCmsPage(data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching kontakt_side:', err);
      }
    };
    fetchPage();
  }, [lang]);

  const seoTitle = lang === 'da'
    ? 'Kontakt os – Tidsbestilling og henvendelse | Nordisk Psykiatri'
    : 'Kontakta oss – Tidsbokning och förfrågan | Nordisk Psykiatri';

  const seoDesc = lang === 'da'
    ? 'Kontakt Nordisk Psykiatri på Vesterbrogade 37 i København. Ring på tlf. +45 23 95 77 07 eller send en besked for tidsbestilling til samtale og udredning.'
    : 'Kontakta Nordisk Psykiatri på Vesterbrogade 37 i Köpenhamn. Ring på tfn. +45 23 95 77 07 eller skicka ett meddelande för tidsbokning till utredning och behandling.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });
  
  // Handle hash scrolling
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#akut-hjaelp') {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = lang === 'da' ? 'Navn er påkrævet' : 'Namn krävs';
    if (!formData.phone.trim()) {
      newErrors.phone = lang === 'da' ? 'Telefonnummer er påkrævet' : 'Telefonnummer krävs';
    } else if (!/^[0-9+\s-]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = lang === 'da' ? 'Ugyldigt telefonnummer' : 'Ogiltigt telefonnummer';
    }
    if (!formData.email.trim()) {
      newErrors.email = lang === 'da' ? 'E-mail er påkrævet' : 'E-post krävs';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = lang === 'da' ? 'Ugyldig e-mailadresse' : 'Ogiltig e-postadress';
    }
    if (!formData.description.trim()) newErrors.description = lang === 'da' ? 'Beskrivelse er påkrævet' : 'Beskrivning krävs';
    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus('submitting');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', description: '', website_url: '' });
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    }
  };

  const formatImageUrl = (img, width = 1920, fallback = '') => {
    const raw = Array.isArray(img) ? img[0] : img;
    if (!raw || typeof raw !== 'string') return fallback;
    const trimmed = raw.trim();
    if (!trimmed) return fallback;
    if (trimmed.includes('/cdn-cgi/image/')) return trimmed;
    if (trimmed.startsWith('https://assets.auxodev.dk/')) {
      return trimmed.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    if (trimmed.startsWith('/_assets/')) {
      return `/cdn-cgi/image/width=${width},quality=85,format=auto${trimmed}`;
    }
    return trimmed;
  };

  const heroEyebrow = cmsPage ? (getLocalizedValue(cmsPage.hero_badge) || (lang === 'da' ? 'KONTAKT OS' : 'KONTAKTA OSS')) : (lang === 'da' ? 'KONTAKT OS' : 'KONTAKTA OSS');
  const heroTitle = cmsPage ? (getLocalizedValue(cmsPage.hero_overskrift) || t('contact.hero_title')) : t('contact.hero_title');
  const heroDescription = cmsPage ? (getLocalizedValue(cmsPage.hero_beskrivelse) || t('contact.hero_desc')) : t('contact.hero_desc');
  const heroBgImage = formatImageUrl(
    cmsPage?.hero_billede,
    1920,
    "https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/inlineimage.png"
  );

  return (
    <div className="flex flex-col min-h-screen bg-sand-light">
      <MedicalClinicSchema />
      {/* HERO */}
      <FluidSubpageHero
        dark={true}
        eyebrow={heroEyebrow}
        title={heroTitle}
        description={heroDescription}
        bgImageUrl={heroBgImage}
        bgImageAlt={lang === 'da' ? 'Nordisk Psykiatri klinik facade' : 'Nordisk Psykiatri klinik fasad'}
        bgImageClass="object-cover object-[center_38%]"
        bgOverlayClass="bg-gradient-to-r from-[#0a1e19]/95 via-[#0a1e19]/60 to-transparent sm:from-[#0a1e19]/90 sm:via-[#0a1e19]/50 sm:to-transparent"
        showBgOverlay={true}
      />

      {/* Main Content Section */}
      <section className="pt-6 sm:pt-8 lg:pt-10 pb-16 md:pb-24 bg-sand-light">
        <div className="mx-auto max-w-[1180px] px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Contact Info Column */}
          <div className="lg:col-span-5 flex flex-col gap-10 text-left">
            <div>
              <h2 className="font-display text-2xl font-medium text-petrol-dark mb-6">
                {cmsPage ? (getLocalizedValue(cmsPage.kontaktinfo_overskrift) || t('contact.info_title')) : t('contact.info_title')}
              </h2>
              
              <ul className="divide-y divide-sand-medium/60 border-y border-sand-medium/60">
                {/* Address */}
                <li className="flex items-start gap-5 py-6">
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-sand-medium/50 flex items-center justify-center text-petrol-medium text-lg" aria-hidden="true">
                    <i className="fa-light fa-map-pin" />
                  </div>
                  <div>
                    <h4 className="font-display text-sm font-semibold text-petrol-dark uppercase tracking-wider">
                      {t('contact.address_label')}
                    </h4>
                    <p className="text-sm text-text-dark/80 mt-1.5 leading-relaxed font-light">
                      Vesterbrogade 37, 1. tv<br />
                      1620 København V
                    </p>
                  </div>
                </li>
                
                {/* Phone */}
                <li className="flex items-start gap-5 py-6">
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-sand-medium/50 flex items-center justify-center text-petrol-medium text-lg" aria-hidden="true">
                    <i className="fa-light fa-phone" />
                  </div>
                  <div>
                    <h4 className="font-display text-sm font-semibold text-petrol-dark uppercase tracking-wider">
                      {t('contact.phone_label')}
                    </h4>
                    <p className="text-sm text-text-dark/80 mt-1.5 font-light">
                      <a href="tel:+4523957707" className="hover:text-petrol-medium transition-colors whitespace-nowrap">
                        +45 23 95 77 07
                      </a>
                    </p>
                  </div>
                </li>
                
                {/* Email */}
                <li className="flex items-start gap-5 py-6">
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-sand-medium/50 flex items-center justify-center text-petrol-medium text-lg" aria-hidden="true">
                    <i className="fa-light fa-envelope" />
                  </div>
                  <div>
                    <h4 className="font-display text-sm font-semibold text-petrol-dark uppercase tracking-wider">
                      {t('contact.email_label')}
                    </h4>
                    <p className="text-sm text-text-dark/80 mt-1.5 font-light">
                      <a href="mailto:kontakt@nordiskpsykiatri.dk" className="hover:text-petrol-medium transition-colors whitespace-nowrap">
                        kontakt@nordiskpsykiatri.dk
                      </a>
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            {/* Opening Hours */}
            <div className="border-t border-sand-medium/60 pt-8">
              <h3 className="font-display text-lg font-medium text-petrol-dark mb-4">
                {t('contact.hours_title')}
              </h3>
              <ul className="space-y-3.5 text-sm text-text-dark/80">
                <li className="flex justify-between items-center py-1.5 border-b border-sand-light">
                  <span className="flex items-center gap-2.5 font-light">
                    <i className="fa-light fa-clock text-xs text-dusty-blue" />
                    {t('contact.hours_mon_thu')}
                  </span>
                  <span className="font-medium text-petrol-dark">08:00 - 16:00</span>
                </li>
                <li className="flex justify-between items-center py-1.5 border-b border-sand-light">
                  <span className="flex items-center gap-2.5 font-light">
                    <i className="fa-light fa-clock text-xs text-dusty-blue" />
                    {t('contact.hours_sun')}
                  </span>
                  <span className="font-medium text-petrol-dark">09:00 - 16:00</span>
                </li>
                <li className="flex justify-between items-center py-1.5 border-b border-sand-light">
                  <span className="flex items-center gap-2.5 font-light">
                    <i className="fa-light fa-clock text-xs text-dusty-blue" />
                    {t('contact.hours_fri_sat')}
                  </span>
                  <span className="font-medium text-text-muted">{t('contact.hours_closed')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="lg:col-span-7 bg-sand-light border border-[#ece8e1] rounded-lg p-8 md:p-10 text-left shadow-[0_8px_30px_rgba(70,58,42,0.025)]">
            <h2 className="font-display text-2xl font-light text-petrol-dark mb-2">
              {cmsPage ? (getLocalizedValue(cmsPage.formular_overskrift) || t('contact.form_title')) : t('contact.form_title')}
            </h2>
            <p className="text-sm text-text-muted font-light mb-8">
              {cmsPage ? (getLocalizedValue(cmsPage.formular_undertekst) || t('contact.form_desc')) : t('contact.form_desc')}
            </p>

            {status === 'success' ? (
              <div className="p-6 rounded-lg bg-emerald-50/60 border border-emerald-100 text-emerald-800 flex items-start gap-3.5">
                <i className="fa-light fa-circle-check text-xl text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-sm font-light leading-relaxed">{t('contact.form_success')}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field (absolutely hidden from humans) */}
                <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                  <label htmlFor="website_url">Website URL</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    autoComplete="off"
                  />
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-xs uppercase tracking-wider font-semibold text-petrol-dark/80 mb-2">
                    {t('contact.form_name')}
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-sand-light/30 px-4 py-3 text-sm text-text-dark focus:border-petrol-medium focus:bg-white focus:outline-none transition-all duration-200 ${
                      errors.name ? 'border-red-300 bg-red-50/10' : 'border-[#ece8e1]'
                    }`}
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1.5 font-light">{errors.name}</p>}
                </div>

                {/* Grid for Phone & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-xs uppercase tracking-wider font-semibold text-petrol-dark/80 mb-2">
                      {t('contact.form_phone')}
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full rounded-lg border bg-sand-light/30 px-4 py-3 text-sm text-text-dark focus:border-petrol-medium focus:bg-white focus:outline-none transition-all duration-200 ${
                        errors.phone ? 'border-red-300 bg-red-50/10' : 'border-[#ece8e1]'
                      }`}
                    />
                    {errors.phone && <p className="text-xs text-red-500 mt-1.5 font-light">{errors.phone}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs uppercase tracking-wider font-semibold text-petrol-dark/80 mb-2">
                      {t('contact.form_email')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full rounded-lg border bg-sand-light/30 px-4 py-3 text-sm text-text-dark focus:border-petrol-medium focus:bg-white focus:outline-none transition-all duration-200 ${
                        errors.email ? 'border-red-300 bg-red-50/10' : 'border-[#ece8e1]'
                      }`}
                    />
                    {errors.email && <p className="text-xs text-red-500 mt-1.5 font-light">{errors.email}</p>}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-xs uppercase tracking-wider font-semibold text-petrol-dark/80 mb-2">
                    {t('contact.form_desc_label')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows="5"
                    value={formData.description}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-sand-light/30 px-4 py-3 text-sm text-text-dark focus:border-petrol-medium focus:bg-white focus:outline-none transition-all duration-200 ${
                      errors.description ? 'border-red-300 bg-red-50/10' : 'border-[#ece8e1]'
                    }`}
                  />
                  {errors.description && <p className="text-xs text-red-500 mt-1.5 font-light">{errors.description}</p>}
                </div>

                {status === 'error' && (
                  <div className="p-4 rounded-lg bg-red-50/60 border border-red-100 text-red-800 flex items-center gap-3">
                    <i className="fa-light fa-circle-exclamation text-lg text-red-600 shrink-0" aria-hidden="true" />
                    <p className="text-xs font-light">{t('contact.form_error')}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full inline-flex items-center justify-center rounded-full bg-petrol-dark px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white shadow-sm hover:bg-petrol-medium transition-all duration-300 gap-2.5 disabled:bg-petrol-medium/50 disabled:cursor-not-allowed cursor-pointer"
                >
                  <span>{status === 'submitting' ? t('contact.form_submitting') : t('contact.form_submit')}</span>
                  <i className="fa-light fa-paper-plane text-xs" aria-hidden="true" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="pt-4 sm:pt-6 lg:pt-8 pb-16 lg:pb-24 bg-sand-light border-t border-sand-medium/60">
        <div className="mx-auto max-w-[1180px] px-6 text-left">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-2xl font-light text-petrol-dark">
              {lang === 'da' ? 'Find klinikken' : 'Hitta kliniken'}
            </h2>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Nordisk+Psykiatri,+Vesterbrogade+37,+1620+K%C3%B8benhavn+V"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs sm:text-sm font-semibold text-petrol-dark hover:text-petrol-medium underline underline-offset-4 transition-colors"
            >
              {lang === 'da' ? 'Åbn i Google Maps' : 'Öppna i Google Maps'} ↗
            </a>
          </div>
          <div className="relative h-[450px] w-full rounded-lg overflow-hidden border border-[#ece8e1] shadow-[0_8px_25px_rgba(70,58,42,0.015)] bg-sand-light">
            <iframe
              src="https://maps.google.com/maps?q=Nordisk+Psykiatri,+Vesterbrogade+37,+1620+K%C3%B8benhavn+V&t=&z=16&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nordisk Psykiatri Map"
              className="absolute inset-0 w-full h-full grayscale hover:grayscale-0 transition-all duration-500 opacity-90 hover:opacity-100"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Practical Information: Akut hjælp */}
      <section className="pt-4 sm:pt-6 lg:pt-8 pb-16 lg:pb-24 bg-sand-light border-t border-sand-medium/60">
        <div className="mx-auto max-w-[1180px] px-6">
          <div className="max-w-3xl mx-auto">
            {/* Akut hjælp */}
            <div id="akut-hjaelp" className="bg-sand-light border border-red-100 rounded-lg p-8 md:p-10 shadow-[0_8px_25px_rgba(70,58,42,0.015)] flex flex-col justify-between text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-red-50/30 rounded-full -mr-8 -mt-8 pointer-events-none" />
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-11 h-11 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-lg" aria-hidden="true">
                    <i className="fa-light fa-handshake-angle" />
                  </div>
                  <h2 className="font-display text-2xl font-light text-red-950">
                    {lang === 'da' ? 'Akut hjælp' : 'Akut hjälp'}
                  </h2>
                </div>
                <p className="text-sm sm:text-base text-text-dark/80 leading-relaxed font-light mb-6">
                  {lang === 'da'
                    ? 'Bemærk venligst, at Nordisk Psykiatri ikke er en akutmodtagelse og ikke har døgnåbent. Vi kan derfor ikke håndtere akutte kriser eller akutte psykiatriske tilstande.'
                    : 'Observera att Nordisk Psykiatri inte är en akutmottagening och inte har dygnet-runt-öppet. Vi kan därför inte hantera akuta kriser eller akuta psykiatriska tillstånd.'}
                </p>
                <div className="bg-red-50/30 border border-red-100/60 rounded-lg p-5 space-y-3.5 text-sm mb-6">
                  <p className="font-semibold text-red-950">
                    {lang === 'da' ? 'Hvis du har akut brug for hjælp, skal du kontakte:' : 'Om du är i akut behov av hjälp, vänligen kontakta:'}
                  </p>
                  <ul className="space-y-2.5 text-text-dark/90 font-light">
                    <li className="flex items-start gap-2.5">
                      <span className="text-red-600 font-bold">•</span>
                      <span><strong>{lang === 'da' ? 'Lægevagten eller Akuttelefonen 1813' : 'Akuttelefonen 1813'}</strong> {lang === 'da' ? '(i Region Hovedstaden)' : '(i Region Hovedstaden)'}</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-red-600 font-bold">•</span>
                      <span><strong>{lang === 'da' ? 'Psykiatrisk Akutmodtagelse' : 'Psykiatrisk akutmottagning'}</strong> {lang === 'da' ? '(på dit nærmeste hospital)' : '(på ditt närmaste sjukhus)'}</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="text-red-600 font-bold">•</span>
                      <span><strong>{lang === 'da' ? 'Livslinien' : 'Livslinien'}</strong> {lang === 'da' ? 'på tlf.' : 'på tlf.'} <a href="tel:+4570201201" className="font-semibold underline hover:text-red-700">70 201 201</a> {lang === 'da' ? '(hvis du har svære tanker)' : '(om du har svåra tankar)'}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-5 border-t border-sand-medium/40">
                <span className="text-xs text-text-muted font-light">
                  {lang === 'da' ? 'Ring altid 112 ved livstruende situationer.' : 'Ring alltid 112 vid livshotande situationer.'}
                </span>
                <a 
                  href="/akut-hjaelp"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 hover:text-red-900 transition-colors"
                >
                  {lang === 'da' ? 'Se fuld akutvejledning' : 'Se full akutvägledning'}
                  <i className="fa-solid fa-arrow-right text-xs" />
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
